import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue';

import { useExperimentStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { useArbitrage } from '@/helpers/composables/useArbitrage';

import { diffInDays } from '@/helpers/date-helper';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import { shouldClickMask } from '@/components/click-masking/click-masking-config';
import { BuyboxParam, getTranslatableForOfferGraphql } from '@/helpers/offer-helper';

import type { TitleOfferFragment } from './graphql/fragments/Offer.fragment';
import { MonetizationType, PresentationType, ObjectType } from '@/@types/graphql-types';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import {
	observableOptions,
	type OfferVisibilityChangedPayload,
} from '@/helpers/composables/useOfferImpressionTracking';

import { getDisneyPromoText, DisneyPlusId } from '@/helpers/disneyPlusPromo';
import { ModalHelper } from '@/helpers/modal-helper';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

// BUYBOX_EXP_4
import { buyboxExp4Validation } from '@/components/experiments/buybox-exp-4/utils';
import { BuyboxExp4Name, BuyboxExp4Variants } from '@/components/experiments/buybox-exp-4';
// BUYBOX_EXP_4

// BUYBOX_EXP_5
import { buyboxExp5Validation } from '@/components/experiments/buybox-exp-5/utils';
import { BuyboxExp5Name, BuyboxExp5Variants } from '@/components/experiments/buybox-exp-5';
// BUYBOX_EXP_5

//APPLE_BACKDROP_EXP
import { AppleBackdropExpName, AppleBackdropExpVariants } from '@/components/experiments/AppleBackdropExp';
import { appleBackdropAffiliateLinksParams } from '@/components/experiments/AppleBackdropExp/affiliateLinksParams';
import { AppleBackdropExpPropsType } from '@/components/experiments/AppleBackdropExp/types';
import { appleIds } from '@/components/experiments/AppleBackdropExp/helpers';
//APPLE_BACKDROP_EXP
// APPLE_QUALITY_TV_EXP
import { AppleQualityTvExpProps } from '@/components/experiments/QualityTvExp/types';
import { appleQualityTvParams } from '../experiments/QualityTvExp/affiliateParams';
// APPLE_QUALITY_TV_EXP

interface BuyBoxOfferProps {
	/**  Bundles get a different iconUrl, this property is added to the offer in BuyBoxRow */
	offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number };
	offerLinkOverride?: string | null;
	promoted?: boolean;
	objectType: ObjectType;
	hidePresentationTypeLabel?: boolean;
	title?: TitleDetail;
	hideLeavingShowIcon?: boolean;
	// APPLE_BACKDROP_EXP_3
	appleBackdropExpProps?: AppleBackdropExpPropsType;
	// APPLE_BACKDROP_EXP_3
	// APPLE_QUALITY_TV_EXP
	appleQualityTvExpProps?: AppleQualityTvExpProps;
	// APPLE_QUALITY_TV_EXP
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BuyBoxOffer',
  props: {
    offer: null,
    offerLinkOverride: { default: null },
    promoted: { type: Boolean, default: false },
    objectType: null,
    hidePresentationTypeLabel: { type: Boolean, default: false },
    title: null,
    hideLeavingShowIcon: { type: Boolean, default: false },
    appleBackdropExpProps: null,
    appleQualityTvExpProps: null
  },
  emits: ["clickOut", "visibilityChanged"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		data: {
			buyboxOffer: BuyBoxOfferProps['offer'];
			isBundle: boolean;
			isLeavingIconShown: boolean;
			evt: MouseEvent;
		}
	): void;
	(
		e: 'visibilityChanged',
		data: OfferVisibilityChangedPayload & {
			element: BuyBoxOfferProps['offer'] & { isLeavingIconShown: boolean };
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { offer: TitleOfferFragment & { iconUrl?: string; bundleId?: number }, offerLinkOverride: string | null, promoted: boolean, objectType: ObjectType, hidePresentationTypeLabel: boolean, title?: TitleDetail, hideLeavingShowIcon: boolean, appleBackdropExpProps?: AppleBackdropExpPropsType, appleQualityTvExpProps?: AppleQualityTvExpProps }

const PlexPlayerModal = () => import('@/ui-components/PlexPlayerModal.vue');





const { webLocale, country } = useLanguageStore();
const { isLoggedIn } = useUserStore();
// BUYBOX_EXP_4
// BUYBOX_EXP_5
const { activeVariantsWithControlGroup } = useExperimentStore();
// BUYBOX_EXP_4
// BUYBOX_EXP_5

const shouldMaskClicks = computed(() => shouldClickMask(props.offer.package.packageId, country.value));

const { isArbitrating, withArbitrageTracking, trackTagEvent } = useArbitrage();

const offerUrl = computed(() => {
	if (isPlexProvider.value) return undefined;
	if (props.offerLinkOverride) return props.offerLinkOverride;
	if (shouldMaskClicks.value) return props.offer.standardWebURL?.split('?')[0];

	const url = new ClickoutUrl(props.offer)
		.set('uct_country', country.value)
		.set('uct_buybox', props.promoted ? BuyboxParam.Promoted : BuyboxParam.Normal);

	// BUYBOX_EXP_4
	// disabled control variant
	// if (
	// 	activeVariantsWithControlGroup.value[BuyboxExp4Name] === BuyboxExp4Variants.CONTROL &&
	// 	buyboxExp4Validation(
	// 		props.objectType,
	// 		isLoggedIn.value(),
	// 		false,
	// 		[props.offer],
	// 		props.offer.monetizationType === MonetizationType.Cinema,
	// 		!!activeVariantsWithControlGroup.value[BuyboxExp4Name]
	// 	)
	// ) {
	// 	url.set('uct_buybox', BuyboxParam.BuyBoxExp4Control);
	// }
	// BUYBOX_EXP_4

	// BUYBOX_EXP_5
	if (
		activeVariantsWithControlGroup.value[BuyboxExp5Name] === BuyboxExp5Variants.CONTROL &&
		buyboxExp5Validation(
			props.objectType,
			isLoggedIn.value(),
			false,
			[props.offer],
			props.offer.monetizationType === MonetizationType.Cinema,
			!!activeVariantsWithControlGroup.value[BuyboxExp5Name]
		)
	) {
		url.set('uct_buybox', BuyboxParam.BuyBoxExp5Control);
	}
	// BUYBOX_EXP_5
	// APPLE_QUALITY_TV_EXP

	if (props.appleQualityTvExpProps?.active && [2, 350].includes(props.offer.package.packageId)) {
		url.set('uct_ct', appleQualityTvParams[props.appleQualityTvExpProps.variant].offers.ct).set(
			'uct_itsct',
			appleQualityTvParams[props.appleQualityTvExpProps.variant].offers.itsct
		);

		return url.toString();
	}
	//APPLE_QUALITY_TV_EXP

	// APPLE_BACKDROP_EXP

	if (props.appleBackdropExpProps?.appleBackdropExpActive && appleIds.includes(props.offer.package.packageId)) {
		const variant: AppleBackdropExpVariants = activeVariantsWithControlGroup.value[AppleBackdropExpName];

		url.set('uct_ct', appleBackdropAffiliateLinksParams[variant].offers.ct).set(
			'uct_itsct',
			appleBackdropAffiliateLinksParams[variant].offers.itsct
		);

		return url.toString();
	}
	//APPLE_BACKDROP_EXP

	if (isArbitrating.value) {
		return withArbitrageTracking(url, () => props.offer).toString();
	}

	return url.toString();
});

const referrerpolicy = computed(() => (shouldMaskClicks.value ? 'no-referrer' : undefined));

const isLeavingIconShown = computed(() => {
	return (
		!props.promoted &&
		(isLoggedIn.value() as boolean) &&
		!!props.offer.availableTo &&
		diffInDays(new Date(props.offer.availableTo)) < 30
	);
});

const packageImg = computed(() => JW_CONFIG.IMAGESCALER_URL + (props.offer.iconUrl ?? props.offer.package.icon));

const isSponsoredOffer = computed(() =>
	SponsoredClickoutsHelper.isSponsoredOfferGraphql(props.offer.package.packageId, webLocale.value)
		? 'nofollow sponsored'
		: 'nofollow'
);

const offerLabel = computed(() => {
	if (isDisneyPromo.value) return disneyPromo.value!.offerText;

	const translation = getTranslatableForOfferGraphql(props.offer, props.objectType);

	if (!translation) return props.offer.retailPrice;

	if (props.offer.monetizationType === MonetizationType.FlatrateAndBuy) {
		return translation + (props.offer?.retailPrice ?? '');
	}

	return translation;
});

const presentationTypeLabel = computed(() => {
	switch (props.offer.presentationType) {
		case PresentationType.Hd:
			return 'HD';
		case PresentationType['4K']:
			return '4K';
		case PresentationType.Dvd:
			return 'DVD';
		case PresentationType.Bluray:
			return 'Blu-Ray';
		default:
			return '';
	}
});

const disneyPromo = computed(() => getDisneyPromoText(country.value));
const isDisneyPromo = computed(
	() => props.offer.package.packageId === DisneyPlusId && !props.offer.bundleId && disneyPromo.value != null
);

function onClickOut(evt: MouseEvent) {
	openTrailerModal();
	trackTagEvent(() => props.offer);

	emit('clickOut', {
		buyboxOffer: props.offer,
		isBundle: !!props.offer.iconUrl,
		isLeavingIconShown: isLeavingIconShown.value,
		evt,
	});
}

/* OFFER IMPRESSION TRACKING */
const observeVisibilityOptions = computed(() => {
	return {
		...observableOptions,
		callback: onVisibilityChanged,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	emit('visibilityChanged', {
		isVisible,
		element: { ...props.offer, isLeavingIconShown: isLeavingIconShown.value },
	});
}

//START AVOD
const isPlexProvider = computed(() => props.offer.package.technicalName === 'plexplayer');

function openTrailerModal() {
	if (!isPlexProvider.value) return;
	ModalHelper.openModal(
		PlexPlayerModal,
		{ offer: props.offer, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
}
//END AVOD

return { __sfc: true,PlexPlayerModal, props, emit, webLocale, country, isLoggedIn, activeVariantsWithControlGroup, shouldMaskClicks, isArbitrating, withArbitrageTracking, trackTagEvent, offerUrl, referrerpolicy, isLeavingIconShown, packageImg, isSponsoredOffer, offerLabel, presentationTypeLabel, disneyPromo, isDisneyPromo, onClickOut, observeVisibilityOptions, onVisibilityChanged, isPlexProvider, openTrailerModal }
}

})